// @givmo/board — whether a person's to-do list is open or minimized, which tab it shows, and whether
// a page has one at all (CBG-UX7-WEB-DIR, CBG-UX7-WEB-OFF; design stop design-268's §7).
//
// ONE store per mounted module (`BoardRuntimeProvider` holds it, as a host holds its QueryClient),
// read through `useSyncExternalStore`, because three places read it: the list itself, the officer
// page frame (`PageShell`) and the director's shell (`DirectorShell`), which both make room for the
// open list on a wide screen and keep the minimized tab clear of a page's last row, so it never
// covers what the page needs (§7, frame t1). Each surface has its own state: an officer who is also a
// director keeps two lists, each where its acts are made (D-CBG-35 (3)).
//
// REMEMBERED PER PERSON, ON THIS DEVICE. Open or minimized is kept in the browser under the surface
// and the account (`board.todo.<surface>.<account>`), read and written inside try/catch: a private
// window or blocked storage leaves the rules below working for the visit, and nothing is lost but the
// memory. Never a server preference: a phone and a laptop may well want it differently, and nothing
// about it is the organization's. Before the account is known nothing is read or written.
//
// THE RULES (§7):
//   * a first visit opens it when something waits, and starts minimized when nothing does — but a
//     director's first visit to a board opens it even when nothing waits, so the list can say what it
//     is (D-CBG-40, Jonathan 2026-10-24 14:40 PDT: "it should show right away and even if there is
//     nothing in it, it should tell the new user what the to-do list is"). The board is the list's
//     PLACE (`notice`'s last argument); the places it has been shown in are kept with the rest, so the
//     first visit is the first on this device, and with storage refused, the first of the visit;
//   * minimizing or opening is remembered;
//   * below the wide screen, choosing an item minimizes it, so the page it opens is fully in view
//     (remembered too: the person used the list to go somewhere);
//   * something NEW opens it once, on the person's next Board page: an item the list hasn't shown on
//     this device before (a consent just circulated, a member just added) opens a minimized list when
//     the person ARRIVES at a page (the list's answers there until its reads have answered once), so
//     an email that sends someone here never leaves its item behind a tab. An item that appears while
//     they stay on a page — their own act made it, or a read on focus brought it — opens nothing
//     there: an open list shows it (it is then seen), a minimized one keeps it, unseen, for their
//     next Board page, unless they open it first (it is then seen). Nothing reopens it again: that's
//     the whole rule, never a nag. The first rule is this one on a device that has seen nothing;
//   * a page that IS an item (a Board Ask's own page) is already in view: that item opens nothing
//     there, a first visit included, so the list never covers the page the email sent them to. The
//     item counts as shown, and the place stays unvisited for their next Board page;
//   * an arrival the person made by CHOOSING from the list (an item's link, or FIX74's Open) counts
//     what that choice brought as shown, so the list stays as they left it (review-274's F1, the
//     orchestrator's ruling, 2026-10-23).

import {
  createContext,
  use,
  useLayoutEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';

export type ToDoSurface = 'officer' | 'director';
export type ToDoTab = 'todo' | 'completed';

export interface ToDoSurfaceState {
  /** `null` until the list has decided (its first answer is in, or the person chose). */
  open: boolean | null;
  tab: ToDoTab;
  /** How many lists of this surface are on the page (a count, so a remount never drops it). */
  present: number;
}

/** What is kept on the device, per surface and account. */
interface Remembered {
  open: boolean;
  /** The items the list has shown on this device, by their stable identities. */
  seen: string[];
  /**
   * The places (a director's boards) the list has been shown in on this device; `null` on a memory
   * kept before places were (it knows the list, not where), which counts every place as visited.
   */
  places: string[] | null;
}

/** No more identities than this are kept: the list's own items are far fewer. */
const SEEN_LIMIT = 200;
/** No more places than this: an account holds a seat on a handful of boards. */
const PLACES_LIMIT = 50;

/** The key a surface's memory is kept under, for one account. */
export function rememberedKey(surface: ToDoSurface, account: string): string {
  return `board.todo.${surface}.${account}`;
}

function readRemembered(surface: ToDoSurface, account: string): Remembered | null {
  try {
    const raw = window.localStorage.getItem(rememberedKey(surface, account));
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const { open, seen, places } = parsed as Record<string, unknown>;
    if (typeof open !== 'boolean') return null;
    return {
      open,
      seen: Array.isArray(seen) ? seen.filter((id): id is string => typeof id === 'string') : [],
      places: Array.isArray(places)
        ? places.filter((place): place is string => typeof place === 'string')
        : null,
    };
  } catch {
    return null;
  }
}

/** Keep `open` and `seen`, and add `place` to the places the memory already holds. */
function writeRemembered(
  surface: ToDoSurface,
  account: string,
  remembered: { open: boolean; seen: readonly string[] },
  place?: string,
) {
  try {
    const before = readRemembered(surface, account)?.places ?? [];
    const places = place === undefined || before.includes(place) ? before : [...before, place];
    window.localStorage.setItem(
      rememberedKey(surface, account),
      JSON.stringify({
        open: remembered.open,
        seen: remembered.seen.slice(0, SEEN_LIMIT),
        places: places.slice(-PLACES_LIMIT),
      }),
    );
  } catch {
    // A private window or blocked storage: the list works for this visit and remembers nothing.
  }
}

export interface ToDoStore {
  subscribe: (listener: () => void) => () => void;
  get: (surface: ToDoSurface) => ToDoSurfaceState;
  /**
   * The list's answer is in, on `page` (the address the person is on); `settled`: none of its reads
   * is reading again. On ARRIVAL at a page (its answers there until one is settled, or under another
   * account) an item it hasn't shown on this device opens it (once); otherwise it stays as it is, or
   * as it was left last time, and every item it holds is then shown, so remembered as seen. An answer
   * that comes while the person stays on the page opens nothing: an open list shows what it holds
   * (seen); a minimized one keeps a new item, unseen, for their next Board page. The answers after a
   * choice from the list (`chose`), to the first settled one, count all it holds as shown. `account`
   * null (not yet known): decided for this visit only. `place` (a director's board): an arrival in a
   * place the list has never been shown in opens it, though it holds nothing. `inView`: the identity
   * of the item this page is, which opens nothing here.
   */
  notice: (
    surface: ToDoSurface,
    account: string | null,
    identities: readonly string[],
    page: string,
    settled?: boolean,
    place?: string,
    inView?: string,
  ) => void;
  /**
   * The person chose from the list (an item's link, or FIX74's Open): the answers that choice brings
   * count as shown, so the list stays as they left it. `false` withdraws the choice (one that brought
   * nothing to read, such as a dismissed confirmation).
   */
  chose: (surface: ToDoSurface, choosing?: boolean) => void;
  /** Open or minimize the list, and remember it; opened, it shows what it holds (seen). */
  setOpen: (surface: ToDoSurface, account: string | null, open: boolean) => void;
  setTab: (surface: ToDoSurface, tab: ToDoTab) => void;
  /** One more (or one fewer) list of this surface on the page. */
  present: (surface: ToDoSurface, delta: 1 | -1) => void;
}

export function createToDoStore(): ToDoStore {
  const initial = (): ToDoSurfaceState => ({ open: null, tab: 'todo', present: 0 });
  let states: Record<ToDoSurface, ToDoSurfaceState> = {
    officer: initial(),
    director: initial(),
  };
  const listeners = new Set<() => void>();
  const update = (surface: ToDoSurface, change: Partial<ToDoSurfaceState>) => {
    states = { ...states, [surface]: { ...states[surface], ...change } };
    for (const listener of listeners) listener();
  };
  /** Where each surface's list last settled (`<account>|<page>`): an answer anywhere else arrives. */
  const answeredOn: Record<ToDoSurface, string | null> = { officer: null, director: null };
  /** A choice from the list, waiting for the answers it brings. */
  const choosing: Record<ToDoSurface, boolean> = { officer: false, director: false };
  /** What each list holds now (its last answer): shown when the person opens it. */
  const holding: Record<
    ToDoSurface,
    { account: string | null; identities: readonly string[] } | null
  > = { officer: null, director: null };
  /** What each list showed in this visit, per account: the memory a refused storage can't keep. */
  const shownThisVisit = new Map<string, readonly string[]>();
  /** The places each list was shown in this visit, per account: the same memory, for places. */
  const placesThisVisit = new Map<string, Set<string>>();
  const show = (
    surface: ToDoSurface,
    account: string | null,
    identities: readonly string[],
    open: boolean,
    place?: string,
  ) => {
    const who = `${surface}|${account ?? ''}`;
    shownThisVisit.set(who, [...identities]);
    if (place !== undefined) {
      const places = placesThisVisit.get(who) ?? new Set<string>();
      places.add(place);
      placesThisVisit.set(who, places);
    }
    if (account !== null) writeRemembered(surface, account, { open, seen: [...identities] }, place);
  };
  /** Whether the list has been shown in `place` before: in this visit, or on this device. */
  const visited = (
    surface: ToDoSurface,
    account: string | null,
    remembered: Remembered | null,
    place: string,
  ) =>
    placesThisVisit.get(`${surface}|${account ?? ''}`)?.has(place) === true ||
    (remembered !== null && (remembered.places === null || remembered.places.includes(place)));

  return {
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    get: (surface) => states[surface],
    notice: (surface, account, identities, page, settled = true, place, inView) => {
      holding[surface] = { account, identities };
      const where = `${account ?? ''}|${page}`;
      // An arrival lasts until the list's reads have answered on the page: a list mounted again shows
      // what it held while it reads, and that read's answer is the arrival's too.
      const arrival = answeredOn[surface] !== where;
      if (settled) answeredOn[surface] = where;
      const current = states[surface].open;
      const remembered = account === null ? null : readRemembered(surface, account);
      if (choosing[surface]) {
        // What the person's own choice from the list brought: shown, the list as they left it.
        if (settled) choosing[surface] = false;
        show(surface, account, identities, current ?? remembered?.open ?? false, place);
        return;
      }
      if (!arrival) {
        // The same page: nothing opens the list here. Open, it shows what it holds; minimized, a new
        // item waits, unseen, for the person's next Board page.
        if (current === true) show(surface, account, identities, true, place);
        return;
      }
      const shownBefore = shownThisVisit.get(`${surface}|${account ?? ''}`) ?? [];
      const seen = new Set([...(remembered?.seen ?? []), ...shownBefore]);
      const somethingNew = identities.some(
        (identity) => identity !== inView && !seen.has(identity),
      );
      // D-CBG-40: the first visit to a place opens the list, so it can say what it is. Not over the
      // page an item IS: that first visit is the next Board page's.
      const firstVisit =
        inView === undefined &&
        place !== undefined &&
        !visited(surface, account, remembered, place);
      const open = somethingNew || firstVisit ? true : (current ?? remembered?.open ?? false);
      show(surface, account, identities, open, inView === undefined ? place : undefined);
      if (open !== current) update(surface, { open });
    },
    chose: (surface, on = true) => {
      choosing[surface] = on;
    },
    setOpen: (surface, account, open) => {
      const held = holding[surface];
      if (open && held !== null && held.account === account) {
        // Opened, it shows what it holds: seen, so an item that waited never reopens it later.
        show(surface, account, held.identities, true);
      } else if (account !== null) {
        const remembered = readRemembered(surface, account);
        writeRemembered(surface, account, { open, seen: remembered?.seen ?? [] });
      }
      if (states[surface].open !== open) update(surface, { open });
    },
    setTab: (surface, tab) => {
      if (states[surface].tab !== tab) update(surface, { tab });
    },
    present: (surface, delta) => {
      update(surface, { present: Math.max(0, states[surface].present + delta) });
    },
  };
}

const ToDoStoreContext = createContext<ToDoStore | null>(null);

/** Holds the module's one store for as long as the module is mounted (`BoardRuntimeProvider`). */
export function ToDoStoreProvider({ children }: { children: ReactNode }) {
  const [store] = useState(createToDoStore);
  return <ToDoStoreContext value={store}>{children}</ToDoStoreContext>;
}

/** The module's store; null outside Board's runtime (a host page that mounts no list). */
export function useToDoStore(): ToDoStore | null {
  return use(ToDoStoreContext);
}

const NOTHING: ToDoSurfaceState = { open: null, tab: 'todo', present: 0 };
const noSubscription = () => () => {};

/** A surface's list state, re-rendering on every change. */
export function useToDoState(surface: ToDoSurface): ToDoSurfaceState {
  const store = useToDoStore();
  return useSyncExternalStore(
    store?.subscribe ?? noSubscription,
    () => store?.get(surface) ?? NOTHING,
    () => store?.get(surface) ?? NOTHING,
  );
}

/**
 * Mark the surface's list as on the page while the calling component is mounted. Before paint, so a
 * page frame makes its room in the same frame the list first draws.
 */
export function useToDoPresence(surface: ToDoSurface) {
  const store = useToDoStore();
  useLayoutEffect(() => {
    if (store === null) return;
    store.present(surface, 1);
    return () => store.present(surface, -1);
  }, [store, surface]);
}

/**
 * What a page frame needs: whether this surface's list is on the page (its foot keeps the tab's room)
 * and whether it is open (on a wide screen the page narrows beside it; the frame's own `xl:` classes
 * say "wide").
 */
export function useToDoLayout(surface: ToDoSurface): { shown: boolean; docked: boolean } {
  const state = useToDoState(surface);
  const shown = state.present > 0;
  return { shown, docked: shown && state.open === true };
}
